import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Flex,
} from '@optiaxiom/react';

import { FormInput } from '@frontend/components';
import { useOpalUser } from '@frontend/hooks';

import { useCreateScopeBundle, useUpdateScopeBundle } from '../../hooks';
import type { ScopeBundle } from '../../types';

type ScopeBundleModalProps = {
  open: boolean;
  onClose: () => void;
  providerId?: string;
  providerName?: string;
  bundle?: ScopeBundle;
};

export function ScopeBundleModal({
  open,
  onClose,
  providerId,
  providerName,
  bundle,
}: ScopeBundleModalProps) {
  const { opalUser } = useOpalUser();
  const isEditing = !!bundle;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    scopes: '',
  });

  useEffect(() => {
    if (bundle) {
      setFormData({
        name: bundle.name,
        description: bundle.description,
        scopes: bundle.scopes,
      });
    } else {
      setFormData({ name: '', description: '', scopes: '' });
    }
  }, [bundle, open]);

  const { createBundle, isCreating } = useCreateScopeBundle(onClose);
  const { updateBundle, isUpdating } = useUpdateScopeBundle(onClose);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!providerId) return;

    if (isEditing && bundle) {
      updateBundle({
        bundleId: bundle.id,
        payload: {
          name: formData.name,
          description: formData.description,
          scopes: formData.scopes,
        },
      });
    } else {
      createBundle({
        provider_id: providerId,
        name: formData.name,
        description: formData.description,
        scopes: formData.scopes,
        created_by: opalUser.email,
      });
    }
  };

  const isSubmitting = isCreating || isUpdating;
  const isValid = formData.name && formData.description && formData.scopes;

  return (
    <Dialog open={open} onOpenChange={open => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          {isEditing ? 'Edit Scope Bundle' : `Add Scope Bundle for ${providerName}`}
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <DialogBody>
            <Flex flexDirection="column" gap="16">
              <FormInput
                name="name"
                label="Bundle Name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="e.g., calendar-read"
                required
              />
              <FormInput
                name="description"
                label="Description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Read access to calendar events"
                required
              />
              <FormInput
                name="scopes"
                label="Scopes (comma-separated)"
                value={formData.scopes}
                onChange={handleInputChange}
                placeholder="calendar.read, calendar.events.readonly"
                required
              />
            </Flex>
          </DialogBody>

          <DialogFooter>
            <DialogClose asChild>
              <Button appearance="subtle">Cancel</Button>
            </DialogClose>
            <Button
              type="submit"
              appearance="primary"
              loading={isSubmitting}
              disabled={!isValid || isSubmitting}
            >
              {isEditing ? 'Save Changes' : 'Create Bundle'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
